import { CSV_ERROR_MESSAGE } from '@/constants/messages';
import { apiClient } from '@/shared/lib/axios';
import type { RezeptHistoryCsvParams } from '@/types/admin-rezept-history';

/**
 * Download rezept history CSV from the admin API.
 * Handles blob response and triggers file download.
 * When the API returns a JSON error (400) in blob mode, parses the blob to extract the error message.
 */
export const downloadRezeptHistoryCsv = async (
  site: string,
  params: RezeptHistoryCsvParams
): Promise<void> => {
  const response = await apiClient.get(`/admin/${site}/rezept-histories/csv`, {
    params: {
      ...params,
      // Laravel boolean validation rejects the string "true"; send 1/0 instead
      forced_output: params.forced_output != null ? (params.forced_output ? 1 : 0) : undefined,
    },
    responseType: 'blob',
  });

  // Check if the response is actually a JSON error (content-type will be application/json)
  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json')) {
    const text = await (response.data as Blob).text();
    const json = JSON.parse(text);
    throw new Error(json.message || CSV_ERROR_MESSAGE);
  }

  // Detect response type (CSV or ZIP) from Content-Type header
  const isZip = contentType.includes('application/zip') || contentType.includes('application/octet-stream');
  const blobType = isZip ? 'application/zip' : 'text/csv;charset=utf-8;';
  const blob = new Blob([response.data], { type: blobType });
  const url = window.URL.createObjectURL(blob);

  // Extract filename from Content-Disposition header; fallback to generated name
  const disposition = response.headers['content-disposition'] || '';
  const filenameMatch =
    disposition.match(/filename\*=UTF-8''(.+)/i) || disposition.match(/filename="?([^";\n]+)"?/i);
  const serverFilename = filenameMatch ? decodeURIComponent(filenameMatch[1].trim()) : null;

  const serviceLabel = params.service_type ?? 'ALL';
  const datestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const ext = isZip ? 'zip' : 'csv';
  const filename =
    serverFilename || `rezept_csv_${serviceLabel}_${params.rezept_yearmonth}_${datestamp}.${ext}`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
